import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTasks, useView, useCreateView, useUpdateView, useDeleteView } from '@/hooks/useTasks.ts';
import { FilterBar } from '@/components/FilterBar.tsx';
import { TaskTable } from '@/components/TaskTable.tsx';
import { BulkBar } from '@/components/BulkBar.tsx';
import { TaskDrawer } from '@/components/TaskDrawer.tsx';
import { QuickCapture } from '@/components/QuickCapture.tsx';
import { PageHeader, Button, Select } from '@/components/ui.tsx';
import { GROUPABLE_FIELDS } from '@shared/types';
import type { TaskFilter, TaskSort, GroupableField } from '@shared/types';

const GROUP_LABELS: Record<GroupableField, string> = {
  project_id: 'Project',
  status: 'Status',
  priority: 'Priority',
  due: 'Due date',
};

const DEFAULT_FILTER: TaskFilter = { top_level_only: true };

/**
 * The analyst view: filter, sort, group, edit inline, act on many at once.
 *
 * Serves both the ad-hoc `/table` route and a saved view at `/view/:id`. The
 * saved view seeds the state and can be updated in place; the ad-hoc route
 * starts from a default filter.
 */
export function TableView() {
  const { id: viewId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openTaskId = searchParams.get('task');

  const { data: savedView } = useView(viewId ?? null);
  const createView = useCreateView();
  const updateView = useUpdateView();
  const deleteView = useDeleteView();

  const [filter, setFilter] = useState<TaskFilter>(DEFAULT_FILTER);
  const [sorts, setSorts] = useState<TaskSort[]>([]);
  const [groupBy, setGroupBy] = useState<GroupableField | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Seed from the saved view once it loads, and re-seed when switching views.
  // Keyed on the id so editing filters does not fight the loaded copy.
  useEffect(() => {
    if (savedView) {
      setFilter(savedView.filter);
      setSorts(savedView.sorts);
      setGroupBy(savedView.group_by);
    } else if (!viewId) {
      setFilter(DEFAULT_FILTER);
      setSorts([]);
      setGroupBy(null);
    }
    setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedView?.id, viewId]);

  const { data: tasks, isPending, isError } = useTasks(filter, sorts);

  // Drop selections for rows that are no longer in the result set, or a bulk
  // action could hit tasks the user can no longer see.
  useEffect(() => {
    if (!tasks) return;
    const visible = new Set(tasks.map((t) => t.id));
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [tasks]);

  function openTask(id: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('task', id);
      return next;
    });
  }

  function closeTask() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('task');
      return next;
    });
  }

  function handleSave() {
    const name = prompt('Name this view:', savedView?.name ?? '');
    if (!name?.trim()) return;

    if (savedView) {
      updateView.mutate({ id: savedView.id, name: name.trim(), filter, sorts, group_by: groupBy });
    } else {
      createView.mutate(
        { name: name.trim(), kind: 'table', filter, sorts, group_by: groupBy },
        { onSuccess: (view) => navigate(`/view/${view.id}`) },
      );
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1440px] px-5 py-8 sm:px-8">
      <PageHeader
        title={savedView?.name ?? 'Table'}
        subtitle="Sort, filter and edit in place. Select rows to act on many at once."
        {...(tasks ? { count: `${tasks.length} shown` } : {})}
        actions={
          <div className="flex items-center gap-1.5">
            <span className="w-40">
              <Select
                value={groupBy ?? ''}
                onChange={(v) => setGroupBy((v || null) as GroupableField | null)}
                ariaLabel="Group by"
                align="right"
                options={[
                  { value: '', label: 'No grouping' },
                  ...GROUPABLE_FIELDS.map((f) => ({ value: f, label: `By ${GROUP_LABELS[f].toLowerCase()}` })),
                ]}
              />
            </span>
            <Button variant="primary" onClick={handleSave}>
              {savedView ? 'Update view' : 'Save view'}
            </Button>
            {savedView && (
              <Button
                variant="danger"
                onClick={() => {
                  if (confirm(`Delete the view "${savedView.name}"? Your tasks are not affected.`)) {
                    deleteView.mutate(savedView.id, { onSuccess: () => navigate('/table') });
                  }
                }}
              >
                Delete
              </Button>
            )}
          </div>
        }
      />

      <QuickCapture defaultStatus="inbox" />

      <FilterBar filter={filter} onChange={setFilter} />

      {isPending && (
        <p className="text-sm" style={{ color: 'var(--color-ink-2)' }}>
          Loading…
        </p>
      )}

      {isError && (
        <p role="alert" className="text-sm" style={{ color: 'var(--color-brick)' }}>
          Could not reach the server.
        </p>
      )}

      {tasks && (
        <TaskTable
          tasks={tasks}
          sorts={sorts}
          onSortChange={setSorts}
          groupBy={groupBy}
          selected={selected}
          onSelectedChange={setSelected}
          onOpen={openTask}
        />
      )}

      <BulkBar selected={selected} onClear={() => setSelected(new Set())} />

      {openTaskId && <TaskDrawer taskId={openTaskId} onClose={closeTask} />}
    </div>
  );
}
